import { useApi, PaginatedResponse } from '@/api';
import {
  CurrencyValue,
  DataTable,
  Flex,
  RelativeDateTime,
  WalletAddress,
  TableSkeleton,
  ArrowRightIcon,
  Box,
  EtherScanPictogram
} from '@blockanalitica/ui';
import { tokensMappingBase, tokensMappingArbitrum } from '@/utils/tokens';
import {
  TablePagination,
  TableSorting
} from '@blockanalitica/ui/package/types/src/components/atoms/Table/types';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';

type EventsTableProps = {
  pagination: TablePagination;
  sorting: TableSorting;
  network: string;
  date: string;
};

type ActivityData = {
  block_number: number;
  datetime: string;
  asset_address_in: string;
  asset_symbol_in: string;
  amount_in: string;
  asset_address_out: string;
  asset_symbol_out: string;
  amount_out: string;
  tx_hash: string;
  event: string;
  order_index: string;
  sender: string;
  receiver: string;
};

export default function EventsTable({
  network,
  pagination,
  sorting,
  date
}: EventsTableProps) {
  const [data, error] = useApi<PaginatedResponse<ActivityData[]>>({
    url: `/sparkstar/sll/psm3s/${network}/events/`,
    queryParams: {
      p: pagination.currentPage,
      p_size: pagination.pageSize,
      order: sorting.currentSorting,
      date: date
    }
  });

  const tokensMapping = (symbol: string) => {
    if (network === 'base') {
      return tokensMappingBase(symbol);
    } else {
      return tokensMappingArbitrum(symbol);
    }
  };

  const scanMapping = (tx_hash: string) => {
    if (network === 'base') {
      return `https://basescan.org/tx/${tx_hash}`;
    } else {
      return `https://arbiscan.io/tx/${tx_hash}`;
    }
  };

  if (error) {
    throw error;
  }

  if (!data) {
    return <TableSkeleton rows={10} columns={6} />;
  }

  const columns: TableColumn[] = [
    {
      header: '',
      getCell: (row) => <WalletAddress address={row.sender} />,
      sticky: true
    },
    {
      header: 'Event',
      getCell: (row) => row.event
    },
    {
      header: (
        <Flex display="flex" flexDirection="row" alignItems="center" gap="3xs">
          Amount In
        </Flex>
      ),
      getCell: (row) => (
        <CurrencyValue
          value={row.amount_in}
          options={{
            currency: tokensMapping(row.asset_symbol_in)
          }}
        />
      ),
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'amount_in'
    },
    {
      header: '',
      getCell: () => (
        <Box color="muted">
          <ArrowRightIcon size="1" />
        </Box>
      ),
      cellAlign: 'center',
      headerAlign: 'center',
      columnSize: ['1.5rem']
    },
    {
      header: (
        <Flex display="flex" flexDirection="row" alignItems="center" gap="3xs">
          Amount Out
        </Flex>
      ),
      getCell: (row) => (
        <CurrencyValue
          value={row.amount_out}
          options={{
            currency: tokensMapping(row.asset_symbol_out)
          }}
        />
      ),
      cellAlign: 'flex-start',
      headerAlign: 'flex-start',
      sortingField: 'amount_out'
    },
    {
      header: 'Time',
      getCell: (row) => (
        <Flex gap={['xs', '2xs']} color="muted">
          <RelativeDateTime value={row.datetime} />
          <a href={scanMapping(row.tx_hash)} target="_blank" rel="noreferrer">
            <EtherScanPictogram size="0" />
          </a>
        </Flex>
      ),
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'order_index'
    }
  ];

  return (
    <DataTable
      rows={data.results}
      columns={columns}
      getKey={(row) => `${row.sender}${row.order_index}`}
      sorting={sorting}
      pagination={{
        ...pagination,
        totalRecords: data.count
      }}
    />
  );
}
